/**
 * Per-visitor conversion deduplication.
 *
 * When a tracking rule has "once per visitor" enabled, the conversion
 * event only fires once per visitor (permanently). Uses localStorage
 * keyed by rule ID.
 *
 * This is NOT automatic: it only applies to rules where the user
 * explicitly enables "once per visitor" in the tracking rule settings.
 */

import { state, debugLog } from "./state.js";
import { mergeSeededRules } from "./conversion-dedup-seed.js";

const STORAGE_KEY = "_tst_cv";

/**
 * Check if this conversion event should be skipped because the same
 * visitor already triggered this rule.
 *
 * Returns true if the rule has oncePerVisitor enabled AND the visitor
 * has already triggered it.
 */
export function shouldSkipForRule(ruleId: string | undefined, oncePerVisitor: boolean | undefined): boolean {
  if (!ruleId || !oncePerVisitor) return false;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;

    const fired: Record<string, number> = JSON.parse(raw);
    if (fired[ruleId]) {
      debugLog("conversionDedup", `skipped rule ${ruleId} (visitor already converted)`);
      return true;
    }
  } catch {
    // localStorage unavailable
  }

  return false;
}

/**
 * Adopt the server's verdict on which rules this visitor already converted on.
 *
 * Call this as soon as the config lands, before any rule can fire.
 *
 * Until 11 August 2026 the browser decided this on its own, and it decided it
 * on weaker evidence than the server had. This store is localStorage: per
 * browser, and Safari deletes script-written storage after seven days of no
 * visit. The server works from the anchor cookie, which it sets itself over
 * HTTP and which lives 390 days. So a visitor coming back after two weeks on
 * Safari found an empty store here, fired the pixel again, and had their
 * server-side copy correctly suppressed. The platform sees a pixel event with
 * no server twin and counts a conversion that did not happen.
 *
 * Merging rather than replacing: the server only knows the visitor by their
 * anchor cookie, and someone who cleared their cookies but kept localStorage
 * is still the same person to us.
 */
export function seedFiredRules(ruleIds: ReadonlyArray<string> | undefined): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const stored: Record<string, number> = raw ? JSON.parse(raw) : {};
    // Absent is not empty, and merging is not replacing: conversion-dedup-seed.ts
    // holds both rules and the test that pins them.
    const merged = mergeSeededRules(stored, ruleIds, Date.now());
    if (!merged) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    debugLog("conversionDedup", "seeded rules from server", JSON.stringify(ruleIds));
  } catch {
    // localStorage unavailable
  }
}

/**
 * Record that this visitor has triggered a rule's conversion event.
 * Call this AFTER successfully sending the event.
 */
export function markRuleFired(ruleId: string | undefined, oncePerVisitor: boolean | undefined): void {
  if (!ruleId || !oncePerVisitor) return;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const fired: Record<string, number> = raw ? JSON.parse(raw) : {};
    fired[ruleId] = Date.now();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(fired));
  } catch {
    // localStorage unavailable
  }
}
